// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Providers ────────────────────────────────────────────────────────
/**
 * Providers an organisation can configure with its own key.
 *
 * `claude_api` is the Anthropic provider. The id is kept from Sprint 47.5.1
 * because every existing settings row and usage-ledger row already carries
 * it; the label the operator sees is "Anthropic".
 */
export const AI_CONFIGURABLE_PROVIDERS = ['claude_api', 'openai', 'openai_compatible'];
/** Every provider Forseti can route to, including the developer relay. */
export const AI_ACTIVE_PROVIDERS = [...AI_CONFIGURABLE_PROVIDERS, 'cli_relay'];
/** Display names. Provider names are data, not page literals. */
export const AI_PROVIDER_LABELS = {
    claude_api: 'Anthropic',
    openai: 'OpenAI',
    openai_compatible: 'OpenAI-compatible',
    cli_relay: 'Local CLI relay (development)',
};
/** Fixed endpoints. Only the OpenAI-compatible provider takes a base URL. */
export const AI_PROVIDER_DEFAULT_BASE_URLS = {
    claude_api: 'https://api.anthropic.com',
    openai: 'https://api.openai.com/v1',
};
// ── Effort and OpenAI options ────────────────────────────────────────
/**
 * Reasoning effort. `default` sends nothing and lets the model decide — it is
 * not an invented provider value. `none`/`minimal` are not offered: GPT-6.1
 * Sol rejects them and Claude has no equivalent.
 */
export const AI_EFFORTS = ['default', 'low', 'medium', 'high', 'xhigh', 'max'];
/** OpenAI API selection. `auto` picks Responses for the GPT-6 family. */
export const AI_OPENAI_API_MODES = ['auto', 'responses', 'chat_completions'];
/** OpenAI reasoning mode (GPT-6 family only). */
export const AI_OPENAI_REASONING_MODES = ['standard', 'pro'];
/** OpenAI service tier: `auto` is the project default, `default` is standard. */
export const AI_OPENAI_SERVICE_TIERS = ['auto', 'default'];
const ALL_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];
const NO_XHIGH = ['low', 'medium', 'high', 'max'];
/**
 * Suggested models per provider. Model ids are free text — a model released
 * after this list is still usable — but an entry here lets the server refuse
 * an effort the model does not accept before any request is sent.
 */
export const AI_MODEL_CATALOG = {
    claude_api: [
        {
            id: 'claude-opus-5-5',
            label: 'Claude Opus 5.5',
            efforts: ALL_EFFORTS,
            thinking: 'always',
            refusal_fallback: true,
            progress_updates: true,
        },
        {
            id: 'claude-sonnet-5-5',
            label: 'Claude Sonnet 5.5',
            efforts: ALL_EFFORTS,
            thinking: 'always',
            fast: true,
            refusal_fallback: true,
            progress_updates: true,
        },
        {
            id: 'claude-haiku-5-5',
            label: 'Claude Haiku 5.5',
            efforts: ALL_EFFORTS,
            thinking: 'always',
            fast: true,
        },
        {
            id: 'claude-fable-5-1',
            label: 'Claude Fable 5.1',
            efforts: ALL_EFFORTS,
            thinking: 'always',
            refusal_fallback: true,
            progress_updates: true,
        },
        { id: 'claude-opus-4-7', label: 'Claude Opus 4.7', efforts: ALL_EFFORTS, thinking: 'adaptive' },
        {
            id: 'claude-sonnet-4-6',
            label: 'Claude Sonnet 4.6',
            efforts: NO_XHIGH,
            thinking: 'adaptive',
        },
        {
            id: 'claude-haiku-4-5',
            label: 'Claude Haiku 4.5',
            efforts: [],
            thinking: 'none',
            fast: true,
        },
    ],
    openai: [
        {
            id: 'gpt-6.1-sol',
            label: 'GPT-6.1 Sol',
            efforts: ALL_EFFORTS,
            thinking: 'none',
            requires_responses: true,
        },
        {
            id: 'gpt-6-astra',
            label: 'GPT-6 Astra',
            efforts: ALL_EFFORTS,
            thinking: 'none',
            requires_responses: true,
        },
        {
            id: 'gpt-6-sol',
            label: 'GPT-6 Sol',
            efforts: ALL_EFFORTS,
            thinking: 'none',
            requires_responses: true,
        },
        {
            id: 'gpt-6-luna',
            label: 'GPT-6 Luna',
            efforts: ALL_EFFORTS,
            thinking: 'none',
            requires_responses: true,
            fast: true,
        },
        { id: 'gpt-4.1', label: 'GPT-4.1', efforts: [], thinking: 'none', fast: true },
    ],
    // Any model the endpoint serves; nothing is suggested or refused up front.
    openai_compatible: [],
};
/** Default model per provider for a fresh configuration. */
export const AI_DEFAULT_MODELS = {
    claude_api: { model: 'claude-opus-5-5', fast_model: 'claude-sonnet-5-5' },
    openai: { model: 'gpt-6.1-sol', fast_model: 'gpt-6-luna' },
    openai_compatible: { model: '' },
};
/**
 * Features that run on the fast model when one is set. Everything else runs
 * on the primary model — this is the same split the hardcoded
 * `DEFAULT_MODEL_BY_FEATURE` table made before (Opus for chat, workflows and
 * redlines; Sonnet for tabular cells and titles).
 */
export const AI_FAST_MODEL_FEATURES = ['tabular', 'title'];
/** True for the GPT-6 family, which runs on the Responses API by default. */
export function isOpenAiResponsesModel(model) {
    return /(?:^|\/)gpt-6(?:\.\d+)?-(?:sol|astra|luna)(?:$|-)/i.test(model);
}
export function findCatalogModel(provider, model) {
    return AI_MODEL_CATALOG[provider].find((m) => m.id === model);
}
/** The model id shape both reference products accept. */
export const AI_MODEL_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:/@-]{0,159}$/;
//# sourceMappingURL=ai-model-settings.js.map